import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchBooks, fetchCategories } from "@/lib/api";
import BookCard from "@/components/BookCard";
import { Search, ArrowDown, Loader2 } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1553729784-e91953dec042?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjByZWFkaW5nJTIwbW9kZXJufGVufDB8fHx8MTc4Njg2NzQyNXww&ixlib=rb-4.1.0&q=85";

export const Home = () => {
  const [searchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      fetchBooks(query, category)
        .then(setBooks)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, category]);

  const layoutMap = useMemo(() => {
    // 8-4, 6-6, 4-4-4 varied pattern
    const patterns = ["hero", "tall", "wide", "wide", "small", "small", "small"];
    return (i) => patterns[i % patterns.length];
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16">
      {/* HERO */}
      <section className="relative">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)] mb-6">
              — the five-page bookshop
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif-display leading-[0.85] tracking-tight"
              style={{ fontSize: "clamp(3.5rem, 12vw, 12rem)" }}
            >
              book
              <span className="italic text-[var(--bw-accent)]">wrck</span>.
            </h1>
            <p className="mt-8 max-w-lg text-base md:text-lg text-[var(--bw-secondary)] leading-relaxed">
              Every great book, distilled into five essential pages. Buy once,
              read forever — knowledge without the fluff.
            </p>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="relative overflow-hidden aspect-[3/4] border border-[var(--bw-border)]">
              <img
                src={HERO_IMAGE}
                alt="reader"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-[var(--bw-bg)]/95 backdrop-blur px-4 py-3 border border-[var(--bw-border)]">
                <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                  Now reading
                </p>
                <p className="font-serif-display text-lg leading-tight mt-1">
                  Six seasons, endless ideas.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div
          id="search"
          data-testid="search-bar-container"
          className="mt-12 md:mt-16 max-w-xl"
        >
          <div className="flex items-center gap-3 border-b border-[var(--bw-primary)]/40 focus-within:border-[var(--bw-accent)] transition-colors py-2">
            <Search className="w-4 h-4 text-[var(--bw-secondary)] shrink-0" />
            <input
              id="book-search"
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, author or tag…"
              className="w-full bg-transparent text-base md:text-lg outline-none text-[var(--bw-primary)] placeholder:text-[var(--bw-secondary)]/70 focus:placeholder:text-[var(--bw-secondary)] py-1"
            />
          </div>
        </div>

        {/* Categories */}
        <div
          data-testid="category-filter-row"
          className="flex flex-wrap gap-2 mt-6"
        >
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`category-btn-${c}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                category === c
                  ? "bg-[var(--bw-primary)] text-[var(--bw-bg)] border-[var(--bw-primary)]"
                  : "border-[var(--bw-border)] hover:border-[var(--bw-primary)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* CATALOG */}
      <section className="mt-16 md:mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
              — the catalog
            </p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none mt-2">
              Currently on the shelf
            </h2>
          </div>
          <p
            data-testid="books-count"
            className="font-mono-plex text-xs md:text-sm text-[var(--bw-secondary)] hidden sm:block"
          >
            {loading ? "loading…" : `${books.length} titles`}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[var(--bw-secondary)] py-20 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-mono-plex text-sm">fetching titles</span>
          </div>
        ) : books.length === 0 ? (
          <div
            data-testid="no-books-message"
            className="py-24 text-center border border-dashed border-[var(--bw-border)]"
          >
            <p className="font-serif-display text-3xl">No books found.</p>
            <p className="text-sm text-[var(--bw-secondary)] mt-2">
              Try a different search or category.
            </p>
          </div>
        ) : (
          <div
            data-testid="books-grid"
            className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-min"
          >
            {books.map((b, i) => (
              <BookCard
                key={b.id}
                book={b}
                size={layoutMap(i)}
                index={i}
              />
            ))}
          </div>
        )}

        <div className="mt-16 flex items-center gap-2 text-sm text-[var(--bw-secondary)]">
          <ArrowDown className="w-4 h-4" />
          <span className="font-mono-plex tracking-widest text-xs uppercase">
            scroll for more shelves soon
          </span>
        </div>
      </section>

      <section
        id="about"
        data-testid="home-about-section"
        className="mt-24 border-t border-[var(--bw-border)] pt-16 grid md:grid-cols-2 gap-12"
      >
        <div>
          <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
            — what is bookwrck?
          </p>
          <h3 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3">
            I read the whole book — you get the five pages that matter.
          </h3>
          <a
            href="/about"
            data-testid="home-about-more-link"
            className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--bw-accent)] hover:underline"
          >
            Read the full story →
          </a>
        </div>
        <div className="space-y-4 text-base md:text-lg text-[var(--bw-secondary)] leading-relaxed">
          <p>
            I read full books cover-to-cover and condense them into short,
            compact notes that capture the core ideas and knowledge of the
            book in just a few pages.
          </p>
          <p>
            You save the twenty hours of reading time and still walk away with
            every key takeaway, framework and memorable line — the whole
            book&apos;s signal, none of the fluff.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Home;
